import { CATEGORY_LABELS, SHOP_ITEMS } from "../data/shopItems";
import type { ShopCategory, ShopEffect, ShopItem } from "../data/shopItems";
import type { GameScene, RoundEndPayload } from "../../scene/src/scenes/GameScene";
import type { CubicleApi } from "../../cubicle/src/cubicle";

interface OwnershipState {
  owned: Set<string>;
  levels: Map<string, number>;
}

export function initShop(scene: GameScene, cubicle: CubicleApi): void {
  const root = document.getElementById("shop");
  if (!root) throw new Error("#shop missing");

  const surplusEl = root.querySelector<HTMLElement>("#shop-surplus")!;
  const roundEl = root.querySelector<HTMLElement>("#shop-round")!;
  const subtitleEl = root.querySelector<HTMLElement>(".shop-header .subtitle")!;
  const tabsEl = root.querySelector<HTMLElement>("#shop-tabs")!;
  const gridEl = root.querySelector<HTMLElement>("#shop-grid")!;
  const launchEl = root.querySelector<HTMLButtonElement>("#shop-launch")!;

  const state: OwnershipState = {
    owned: new Set(),
    levels: new Map(),
  };

  let activeCategory: ShopCategory = "ship";

  const canBuy = (item: ShopItem): { ok: boolean; reason?: string } => {
    if (scene.getSurplus() < item.cost) return { ok: false, reason: "Not enough Surplus" };
    if (item.stackable) {
      const lvl = state.levels.get(item.id) ?? 0;
      if (item.maxLevel !== undefined && lvl >= item.maxLevel) {
        return { ok: false, reason: "Max level" };
      }
    } else if (state.owned.has(item.id)) {
      return { ok: false, reason: "Owned" };
    }
    return { ok: true };
  };

  const applyEffect = (effect: ShopEffect): void => {
    switch (effect.kind) {
      case "cadence":
        scene.upgradeFireRate();
        return;
      case "drive":
        scene.upgradeDrive();
        return;
      case "secondBarrel":
        scene.enableSecondBarrel();
        return;
      case "sharperEye":
        scene.enableSharperEye();
        return;
      case "ricochet":
        scene.enableRicochet();
        return;
      case "forgivingHull":
        scene.upgradeForgivingHull();
        return;
      case "sensorCourtesy":
        scene.enableSensorCourtesy();
        return;
      case "bulletColor":
        scene.setBulletColor(effect.color);
        return;
      case "flareColor":
        scene.setFlareColor(effect.color);
        return;
      case "livery":
        scene.setLiveryColor(effect.color);
        return;
      case "unlockChannel":
        // No-op this build — radio surface is not mounted.
        // Effect retained for resurrection (FUTURE.md § Resurrect O:ALSO chrome).
        return;
      case "decor":
        return;
      case "flereHat": {
        const oalso = window.OALSO as unknown as
          | { flereHat?: { setActive: (k: string) => void } }
          | undefined;
        oalso?.flereHat?.setActive(effect.hatKey);
        return;
      }
    }
  };

  const buy = (item: ShopItem): void => {
    if (!canBuy(item).ok) return;
    if (!scene.spendSurplus(item.cost)) return;
    if (item.stackable) {
      state.levels.set(item.id, (state.levels.get(item.id) ?? 0) + 1);
    } else {
      state.owned.add(item.id);
    }
    applyEffect(item.effect);
    // Tell Flere. Hats get the full react treatment; everything else
    // just lands in the event buffer for the next ambient poll.
    scene.events.emit("shop-purchase", {
      id: item.id,
      name: item.name,
      cost: item.cost,
      is_hat: item.effect.kind === "flereHat",
      hat_key:
        item.effect.kind === "flereHat" ? item.effect.hatKey : undefined,
    });
    render();
  };

  const renderTabs = () => {
    tabsEl.innerHTML = "";
    (Object.keys(CATEGORY_LABELS) as ShopCategory[]).forEach((cat) => {
      const btn = document.createElement("button");
      btn.className = "shop-tab";
      btn.textContent = CATEGORY_LABELS[cat];
      if (cat === activeCategory) btn.classList.add("active");
      btn.addEventListener("click", () => {
        activeCategory = cat;
        render();
      });
      tabsEl.appendChild(btn);
    });
  };

  const renderGrid = () => {
    gridEl.innerHTML = "";
    const items = SHOP_ITEMS.filter((i) => i.category === activeCategory);
    if (items.length === 0) {
      const empty = document.createElement("div");
      empty.className = "shop-empty";
      empty.textContent = "Nothing on offer this week.";
      gridEl.appendChild(empty);
      return;
    }
    for (const item of items) {
      const card = document.createElement("div");
      card.className = "shop-card";
      const lvl = state.levels.get(item.id) ?? 0;
      const owned = state.owned.has(item.id);
      if (owned) card.classList.add("owned");

      const title = document.createElement("strong");
      title.textContent = item.name;
      card.appendChild(title);

      const desc = document.createElement("p");
      desc.textContent = item.description;
      card.appendChild(desc);

      const footer = document.createElement("div");
      footer.className = "shop-card-footer";

      const status = document.createElement("span");
      status.className = "shop-status";
      if (item.stackable) {
        status.textContent = `Lvl ${lvl}${item.maxLevel !== undefined ? ` / ${item.maxLevel}` : ""}`;
      } else if (owned) {
        status.textContent = "Fitted";
      } else {
        status.textContent = "";
      }
      footer.appendChild(status);

      const btn = document.createElement("button");
      const avail = canBuy(item);
      btn.disabled = !avail.ok;
      btn.className = "shop-buy";
      btn.textContent = avail.ok
        ? `${item.cost} surplus`
        : avail.reason === "Owned"
          ? "Fitted"
          : avail.reason === "Max level"
            ? "Max"
            : `${item.cost}`;
      btn.addEventListener("click", () => buy(item));
      footer.appendChild(btn);

      card.appendChild(footer);
      gridEl.appendChild(card);
    }
  };

  const render = () => {
    surplusEl.textContent = scene.getSurplus().toString();
    renderTabs();
    renderGrid();
  };

  scene.events.on("round-end", (payload: RoundEndPayload) => {
    if (payload.round === 0) {
      subtitleEl.textContent = "Pre-flight — Flere's catalogue";
      launchEl.textContent = "Deploy Wave 1";
    } else {
      roundEl.textContent = payload.round.toString();
      subtitleEl.innerHTML =
        `Wave <span id="shop-round">${payload.round}</span> complete — Flere's catalogue`;
      launchEl.textContent = "Deploy Next Wave";
    }
    render();
    root.classList.add("open");
    cubicle.showShop();
  });

  launchEl.addEventListener("click", () => {
    root.classList.remove("open");
    cubicle.hideShop();
    scene.startRound();
  });
}
